// Step 2 of "Add host with installer" and of "Install command" (installer
// ontwerp §1 steps 3 to 6, §2): the one-line command with a Copy button, the
// read-it-first variant, a one-hour countdown, and then the host's progress
// (waiting, pending approval, connected). The token only lives inside the
// command and is shown once; closing the dialog loses it.

import { useEffect, useState, type ReactNode } from 'react'
import type { SandboxHost, SandboxHostInstall } from '@goblic/shared'
import { agentBadge } from './agent-approval-card'
import { StatusBadge } from './status-badge'
import { Notice } from './ui'

/** `1h`-style countdown: "59:12", "0:05", or null once it passed. */
export function formatCountdown(msLeft: number): string | null {
  if (msLeft <= 0) return null
  const total = Math.ceil(msLeft / 1000)
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const seconds = total % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`
}

/** The "read it first" variant: the same download, written to a file (§2.2). */
export function downloadCommands(command: string): string {
  const download = command.split(' | ')[0]?.trim() ?? command
  return [`${download} -o goblic-install.sh`, 'less goblic-install.sh', 'sudo sh goblic-install.sh'].join('\n')
}

function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs)
    return () => window.clearInterval(id)
  }, [intervalMs])
  return now
}

function CopyBlock({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState<'yes' | 'failed' | null>(null)
  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-3">
        <span className="text-sm font-medium text-ink-2">{label}</span>
        <button
          type="button"
          className="btn-secondary btn-sm"
          onClick={() => {
            navigator.clipboard
              .writeText(value)
              .then(() => setCopied('yes'))
              // Plain-http origins have no clipboard: the text is selectable below.
              .catch(() => setCopied('failed'))
              .finally(() => window.setTimeout(() => setCopied(null), 2000))
          }}
        >
          {copied === 'yes' ? 'Copied' : copied === 'failed' ? 'Select and copy' : 'Copy'}
        </button>
      </div>
      <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded-control border border-line bg-sunken px-3 py-2 font-mono text-sm text-ink">
        {value}
      </pre>
    </div>
  )
}

export function InstallCommandPanel({
  host,
  install,
  onScan,
  children,
}: {
  /** The live row, refreshed while the dialog is open. */
  host: SandboxHost
  install: SandboxHostInstall
  /** Opens "Scan & advise" for a connected host. */
  onScan?: () => void
  /** The approval card while the host is pending. */
  children?: ReactNode
}) {
  const now = useNow(1000)
  const left = Date.parse(install.expiresAt) - now
  const countdown = formatCountdown(left)
  const badge = agentBadge(host, now)
  const state = host.agent?.state
  const showCommand = state !== 'pending' && state !== 'connected'

  return (
    <div className="space-y-4">
      {badge ? (
        <div className="flex items-center gap-2">
          <span className="text-sm text-ink-3">{host.name}</span>
          <StatusBadge status={badge.status} label={badge.label} />
        </div>
      ) : null}

      {state === 'connected' ? (
        <Notice tone="success">
          Host is connected.{' '}
          {onScan ? (
            <button type="button" className="underline" onClick={onScan}>
              Scan & advise
            </button>
          ) : null}
        </Notice>
      ) : null}

      {children}

      {showCommand ? (
        <>
          <CopyBlock label="Run this on the new machine" value={install.command} />
          <p className="text-sm text-ink-2">
            {countdown ? (
              <>
                Valid until{' '}
                {new Date(install.expiresAt).toLocaleTimeString(undefined, {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
                , one hour, single use. <span className="tabular-nums">({countdown} left)</span>
              </>
            ) : (
              'This command has expired. Create a new one with Install command.'
            )}
          </p>
          <details className="rounded-control border border-line px-3 py-2">
            <summary className="cursor-pointer text-base text-ink">Prefer to read it first?</summary>
            <div className="mt-3">
              <CopyBlock label="Download, read, then run" value={downloadCommands(install.command)} />
            </div>
          </details>
          <p className="text-sm text-ink-3">
            The token is shown once. If you close this dialog, create a new one with{' '}
            <em>Install command</em>.
          </p>
          <p className="text-sm text-ink-3">
            Behind a proxy? Use <code className="font-mono">sudo -E</code> and set{' '}
            <code className="font-mono">HTTPS_PROXY</code> (see docs).
          </p>
        </>
      ) : null}
    </div>
  )
}
